import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

const HeroSection = ({ onStartQuiz }) => {
  const [currentPoster, setCurrentPoster] = useState(0);
  
  // Featured movie posters for background carousel
  const featuredPosters = [
    "https://image.tmdb.org/t/p/w1280/pFlaoHTZeyNkG83vxsAJiGzfSsa.jpg", // Popular movie
    "https://image.tmdb.org/t/p/w1280/8Gxv8gSFCU0XGDykEGv7zR1n2ua.jpg", // Another popular
    "https://image.tmdb.org/t/p/w1280/rktDFPbfHfUbArZ6OOOKsXcv0Bm.jpg", // Third one
  ];

  // Auto-rotate background posters
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPoster((prev) => (prev + 1) % featuredPosters.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [featuredPosters.length]);

  const heroVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="hero-section position-relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="hero-background">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPoster}
            className="hero-poster"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 0.3, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 1 }}
            style={{
              backgroundImage: `url(${featuredPosters[currentPoster]})`,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(8px)'
            }}
          />
        </AnimatePresence>
        <div className="hero-overlay" />
      </div>

      {/* Hero Content */}
      <Container className="hero-content position-relative">
        <Row className="min-vh-100 align-items-center justify-content-center text-center">
          <Col lg={8}>
            <motion.div
              variants={heroVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Main Title */}
              <motion.h1 
                className="hero-title mb-4"
                variants={floatingVariants}
                animate="float"
              >
                <span className="gradient-text">StreamingMood</span>
                <motion.div 
                  className="title-emoji"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span role="img" aria-label="theater mask">🎭</span>
                </motion.div>
              </motion.h1>

              {/* Subtitle */}
              <motion.p 
                className="hero-subtitle mb-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Discover your perfect movie match based on your <strong>current mood</strong>
                <br />
                <span className="text-muted">AI-powered recommendations from Netflix, Disney+, HBO Max & more</span>
              </motion.p>

              {/* CTA Button - Moved Up for Mobile */}
              <motion.div
                className="mb-5"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size="lg"
                  className="cta-button-enhanced"
                  onClick={onStartQuiz}
                >
                  <span role="img" aria-label="sparkles">✨</span>
                  Find What You Want to Watch
                  <span role="img" aria-label="right arrow">🎬</span>
                </Button>
              </motion.div>

              {/* Features Grid */}
              <motion.div 
                className="features-grid mb-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Row className="g-4">
                  <Col lg={3} md={6}>
                    <div className="feature-card">
                      <div className="feature-icon"><span role="img" aria-label="lightning bolt">⚡</span></div>
                      <h6>Instant Results</h6>
                      <small>Get recommendations in seconds</small>
                    </div>
                  </Col>
                  <Col lg={3} md={6}>
                    <div className="feature-card">
                      <div className="feature-icon"><span role="img" aria-label="target">🎯</span></div>
                      <h6>Mood Matching</h6>
                      <small>Perfect for how you feel right now</small>
                    </div>
                  </Col>
                  <Col lg={3} md={6}>
                    <div className="feature-card">
                      <div className="feature-icon"><span role="img" aria-label="television">📺</span></div>
                      <h6>All Platforms</h6>
                      <small>Netflix, Disney+, Prime & more</small>
                    </div>
                  </Col>
                  <Col lg={3} md={6}>
                    <div className="feature-card">
                      <div className="feature-icon"><span role="img" aria-label="movie camera">🎬</span></div>
                      <h6>Watch Trailers</h6>
                      <small>YouTube trailers for every movie</small>
                    </div>
                  </Col>
                </Row>
              </motion.div>


              {/* Stats Bar */}
              <motion.div 
                className="stats-bar mt-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <Row className="text-center">
                  <Col xs={4}>
                    <div className="stat-item">
                      <h4 className="stat-number">50K+</h4>
                      <small>Movies & Shows</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="stat-item">
                      <h4 className="stat-number">98%</h4>
                      <small>Match Accuracy</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="stat-item">
                      <h4 className="stat-number">15+</h4>
                      <small>Streaming Services</small>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </motion.div>
          </Col>
        </Row>
      </Container>

    </div>
  );
};

export default HeroSection;
